import { upsertConfig } from '../api/me';

/**
 * Every per-member UI dismissal key that used to live in browser localStorage
 * and now lives in the member's `/me/config`. Single source of truth for the
 * one-time migration below.
 */
export const DISMISSAL_KEYS = [
  'wizard-reminder-step1-dismissed',
  'wizard-reminder-step2-dismissed',
  'wizard-reminder-step3-dismissed',
  'wizard-reminder-step4-dismissed',
  'wizard-bottom-reminder-dismissed',
  'configure-reminder-step1-dismissed',
  'configure-reminder-step3-dismissed',
  'configure-reminder-step4-dismissed',
  'quiz-editor-reminder-dismissed',
  'survey-editor-reminder-dismissed',
  'program-wizard-completion-dismissed',
  'program-configure-reminder-step1-dismissed',
  'program-configure-reminder-step3-dismissed',
  'program-configure-reminder-step4-dismissed',
] as const;

const guardKey = (membershipKey: string): string =>
  `tereiko-dismissals-migrated:${membershipKey}`;

/**
 * One-time, best-effort copy of already-dismissed reminder flags from browser
 * localStorage into the member's server-side `/me/config`, so dismissals made
 * before this feature shipped are preserved (and now sync across devices).
 *
 * Idempotent per membership via a localStorage guard flag. `membershipKey`
 * identifies the active membership (the tenant id — one membership per tenant);
 * the server scopes the write to the caller's membership via the JWT. Source
 * keys are not deleted (other memberships may still copy them; nothing reads
 * them anymore). Errors are swallowed — a missed migration just re-shows a
 * reminder, which the member can dismiss again.
 */
export async function migrateDismissals(membershipKey: string): Promise<void> {
  if (typeof window === 'undefined' || !membershipKey) return;
  const guard = guardKey(membershipKey);
  if (localStorage.getItem(guard) === 'done') return;

  const dismissed = DISMISSAL_KEYS.filter((key) => localStorage.getItem(key) === 'true');

  try {
    await Promise.all(
      dismissed.map((key) => upsertConfig({ key, enabled: true }).catch(() => undefined))
    );
    // Mark done even when there was nothing to copy, so we stop checking.
    localStorage.setItem(guard, 'done');
  } catch {
    // Best-effort — leave the guard unset so a later load can retry.
  }
}
